import { CheckableLineItem, LineDamage, lineKey } from '../../../service/receiving-mobile/log-entry-draft.store';
import { ReceivingLogEditReceipt } from '../../../models/logs/ReceivingLogDetails';
import { buildItemsString } from './items-string.rules';

/**
 * log-edit-items-string.rules.ts
 *
 * Recomposes `MM.ReceivingLog.Items` — the human note text — from the log-edit
 * screen's EDITED receipt rows, for `PUT /V1/receiving-log/{logId}` (handoff-18
 * §2). The backend cannot build this string (it carries inferred missing-item
 * text that is never persisted anywhere), so the client owns it, and §2 requires
 * the SAME `items-string.rules.ts` composition the submit runs — not a second
 * one that drifts.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * READ THIS BEFORE CHANGING ANYTHING HERE. §2 IS HONOURED IN PART, NOT IN FULL.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `buildItemsString` composes from `CheckableLineItem` — the Items step's
 * PO-line working model — and emits up to five clauses, joined by `'. '`:
 *
 *   1. `LINE ITEM(S) …`      what ARRIVED, per PO line     ← derived from receipts
 *   2. `ADDITIONAL ITEMS: …` off-PO items                  ← derived from receipts
 *   3. `MISSING LINE ITEM(S) …` what is still owed          ← NOT derivable here
 *   4. free manual-entry text                               ← NOT derivable here
 *   5. `PO COMPLETE`                                        ← NOT derivable here
 *
 * Clauses 1 and 2 ARE reproducible from the receipt ledger, because the receipt
 * rows are what the submit built out of those same lines: this file inverts
 * `receipt-rows.rules.ts` back into `CheckableLineItem`s and hands them to the
 * real rule. Every quantity that reaches the string is a stored fact.
 *
 * Clauses 3–5 are not, and no amount of care makes them so:
 *
 * - The MISSING list needs `quantityExpected` (the PO's canceled-reduced ordered
 *   qty) and the line's PRIOR ledger `quantityReceived`, plus the never-persisted
 *   `outstandingQty` / `missingNotes` the missing page captured. The details
 *   payload has none of them, and handoff §1 is explicit that this GET REPLACES
 *   fetching `po-data`, so they are not available on this screen by design. The
 *   receipt row's `quantityOrdered` is not a substitute: it ignores prior
 *   deliveries entirely, so shortfalls computed from it would be wrong in both
 *   directions.
 * - Manual free text and `PO COMPLETE` were never rows to begin with.
 *
 * So the rule this file follows is: **recompose what the rows determine, and let
 * a HUMAN own what they don't.** {@link splitPreservedNote} strips the LEADING
 * clauses that clauses 1–2 own (and only those — it stops at the first clause it
 * cannot positively identify), then separates the MISSING clause from whatever
 * manual text follows it. The manual text rides through untouched. The MISSING
 * clause is handed to the UI as an EDITABLE, prefilled field, and
 * {@link recomposeItemsNote} takes the user's version back as `missingOverride`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY A TEXT FIELD AND NOT A CALCULATION
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * That clause was NEVER derived from stored data. It came from the user's own
 * selections on the missing page at submit time, and those inputs were never
 * persisted. So the faithful edit affordance is a human judgment at edit time,
 * not arithmetic: nothing here can recompute it, and anything that tried would
 * assert a shortfall nobody measured.
 *
 * What used to happen instead was that the clause was preserved verbatim and
 * went silently stale: change a quantity 6→2 and the note read
 * `LINE ITEM 1 (X2). MISSING LINE ITEM 1 (X4)` when 8 were really owed. The
 * caller closes that by REQUIRING a decision — see `missingClauseNeedsReview` in
 * `receiving-log-save.rules.ts`, which blocks the save whenever the receipt
 * quantities moved and a MISSING clause exists. Turning a silent wrong number
 * into a decision is the whole fix; this module's part of it is only to split the
 * clause out and to take the answer back verbatim.
 *
 * (Prefilling a SUGGESTED number would need `quantityExpected` on the details
 * payload. That is an enhancement to raise, not part of this.)
 *
 * To be explicit about the guard rails, `recomposeItemsNote` calls
 * `buildItemsString` with `manualItems: ''` and `poComplete: false`, and every
 * reconstructed row is stamped `isClosed: true`, which is what makes
 * `isOutstandingLine` (and therefore a DERIVED MISSING clause) unconditionally
 * empty. That stamp is load-bearing: without it, a partially-refused line would
 * emit a MISSING quantity computed from this delivery alone. The only MISSING
 * text that can reach the wire is the stored one or the user's edit of it.
 */

/** The receipt-row facts the note composition reads. Deliberately narrow: no ids, no dates, no PO number. */
export type NoteReceiptRow = Pick<
  ReceivingLogEditReceipt,
  | 'poLineSequence'
  | 'itemNumber'
  | 'itemDescription'
  | 'quantityReceived'
  | 'unitOfMeasure'
  | 'receiptStatus'
  | 'completesLine'
  | 'isDamaged'
  | 'damagedQuantity'
  | 'damageNote'
  | 'damageDisposition'
>;

/** The `CheckableLineItem` rows + damage lookup `buildItemsString` needs, rebuilt from receipt rows. */
export interface ReconstructedNoteLines {
  lineItemRows: CheckableLineItem[];
  damageByLine: Record<string, LineDamage>;
}

/**
 * The clause prefixes `buildItemsString` puts at the FRONT of the note and that
 * this file can rebuild from the receipt rows. Anchored and case-insensitive;
 * the composed string is upper-cased but a hand-edited note may not be.
 */
const DERIVED_CLAUSE_PATTERNS: readonly RegExp[] = [
  /^LINE ITEMS? /i,
  /^ADDITIONAL ITEMS: /i,
];

/**
 * The MISSING clause `buildItemsString` emits, which sits immediately after the
 * derived clauses and before any manual text: `MISSING LINE ITEM 1 (X4)`,
 * `MISSING LINE ITEMS 4, 9 (X1)`. Anchored so it only ever matches a clause in
 * that position — manual text that merely mentions "missing" is not it.
 */
const MISSING_CLAUSE_PATTERN = /^MISSING LINE ITEMS?\b/i;

/** The separator `buildItemsString` joins its clauses with. Splitting and rejoining on it is lossless. */
const CLAUSE_SEPARATOR = '. ';

/**
 * The trailing `PO COMPLETE` clause `buildItemsString` appends last.
 *
 * Split OUT of the manual tail rather than carried through with it, because the
 * edit screen can now change PO completion and therefore has to be able to
 * re-derive this clause. Left in the tail it would be preserved verbatim AND
 * re-appended — a note reading `… PO COMPLETE. PO COMPLETE`.
 */
const PO_COMPLETE_CLAUSE = 'PO COMPLETE';
const PO_COMPLETE_PATTERN = /^PO COMPLETE\.?$/i;

/** The two halves of a stored note that the receipt rows cannot rebuild. */
export interface PreservedNoteParts {
  /**
   * The stored `MISSING LINE ITEM(S) …` clause, or `''` when the note has none.
   *
   * This is what the UI prefills its editable shortfall field with. It is a
   * human's recorded judgment, never a calculation — see the file header.
   */
  missing: string;

  /**
   * Everything after the MISSING clause EXCEPT a trailing `PO COMPLETE`: the
   * manual free text. Never edited, never re-derived, always carried through.
   *
   * That text has no editor anywhere on the edit screen, by design — mobile's own
   * free-text box is a fallback for a PO with no checklist at all, not a field this
   * screen mirrors. So it rides through exactly as stored.
   */
  manual: string;

  /**
   * Whether the stored note ends with `PO COMPLETE`.
   *
   * Extracted so it can be RE-DERIVED from the current completion decision rather
   * than preserved: the edit screen can change PO completion, and a preserved copy
   * plus a derived one is a note that says it twice.
   */
  poComplete: boolean;
}

/**
 * Splits a stored note into the derived head (discarded — the rows rebuild it),
 * the MISSING clause, and the manual tail.
 *
 * Only LEADING clauses are dropped, and only while they match a pattern that
 * positively identifies a clause `buildItemsString` builds from rows. The scan
 * stops at the first clause it cannot identify, so manual text is never eaten
 * even if it happens to contain the word "MISSING" — the one assumption is that
 * manual text does not itself START with `"LINE ITEMS "` or `"ADDITIONAL
 * ITEMS: "`, which it cannot, because `buildItemsString` always places those
 * clauses ahead of it.
 *
 * @param storedNote The note text as it is stored on the log right now.
 */
export function splitPreservedNote(storedNote: string | null | undefined): PreservedNoteParts {
  const clauses = (storedNote ?? '').split(CLAUSE_SEPARATOR);

  let firstKept = 0;
  while (firstKept < clauses.length && isDerivedClause(clauses[firstKept])) {
    firstKept++;
  }

  const rest = clauses.slice(firstKept);

  // A trailing PO COMPLETE comes off the END before anything else is decided, so
  // the flag is reported separately and the clause is never left inside `manual`
  // for a re-derivation to duplicate. Only ever the LAST clause — that is where
  // `buildItemsString` puts it, and matching it anywhere else would eat manual
  // text that happens to say the words.
  let poComplete = false;
  if (rest.length > 0 && PO_COMPLETE_PATTERN.test(rest[rest.length - 1].trim())) {
    poComplete = true;
    rest.pop();
  }

  if (rest.length > 0 && MISSING_CLAUSE_PATTERN.test(rest[0])) {
    return { missing: rest[0], manual: rest.slice(1).join(CLAUSE_SEPARATOR), poComplete };
  }
  return { missing: '', manual: rest.join(CLAUSE_SEPARATOR), poComplete };
}

/**
 * The stored shortfall text alone — what the edit screen prefills its editable
 * MISSING field with, and what {@link recomposeItemsNote} falls back to when no
 * override is given.
 *
 * `''` means the note records no shortfall, which is also the signal that there
 * is nothing for the user to confirm before saving.
 */
export function missingNoteClause(storedNote: string | null | undefined): string {
  return splitPreservedNote(storedNote).missing;
}

function isDerivedClause(clause: string): boolean {
  return DERIVED_CLAUSE_PATTERNS.some((pattern) => pattern.test(clause));
}

/**
 * Recomposes the note text to send as `items` on the `PUT`.
 *
 * Send the result whenever the receipt rows changed, or the stored note keeps
 * describing the rows the user just replaced. OMIT it (don't send `''`) when
 * they didn't — `''` writes an empty note, which is a real edit (§2). Deciding
 * which of those applies is `buildLogEditRequest`'s job, not this function's.
 *
 * The composition is: rebuilt arrived/off-PO clauses, then the shortfall text,
 * then the manual tail. Only the first of those three is derived from rows.
 *
 * @param storedNote The note currently on the log (`details.log.items`).
 * @param effectiveRows The receipt rows as they will be AFTER the save — the
 *   loaded rows with the user's patches applied, deletions removed, and
 *   additions appended. See `effectiveReceiptRows` in
 *   `receiving-log-save.rules.ts`.
 * @param missingOverride The user's shortfall text, taken VERBATIM (trimmed).
 *   `null`/`undefined` means "the user did not touch it", and the stored MISSING
 *   clause is preserved unchanged — which is right for an edit that moved no
 *   quantities. `''` deliberately DROPS the clause: a user who cleared the field
 *   is saying nothing is owed, and that is a real edit, not an omission.
 */
export function recomposeItemsNote(
  storedNote: string | null | undefined,
  effectiveRows: NoteReceiptRow[],
  missingOverride?: string | null,
  poComplete?: boolean | null,
): string {
  const { lineItemRows, damageByLine } = receiptRowsToNoteLines(effectiveRows);

  // manualItems '' and poComplete false: the manual text is not recoverable from
  // rows and lives in the preserved tail instead, and PO completion is appended
  // below from the DECISION rather than let this call derive it — the rows say
  // nothing about whether a whole PO is finished.
  const derived = buildItemsString(lineItemRows, '', false, damageByLine);
  const stored = splitPreservedNote(storedNote);
  const complete = poComplete == null ? stored.poComplete : poComplete;

  // A complete PO has nothing owed by definition, so ticking completion DROPS the
  // shortfall — the same rule `buildItemsString` applies on submit, and the reason a
  // note can never read "PO COMPLETE" and "MISSING LINE ITEM 1 (X4)" at once.
  //
  // Worth being blunt: this discards shortfall text a human typed. That is the
  // intended trade — the tick is the later and more specific statement, and a note
  // contradicting itself is worse than one that drops a clause the tick just
  // falsified. Untick before saving and the stored clause comes back, because it is
  // re-read from `storedNote` every time.
  //
  // ⚠️ Only on an EXPLICIT tick (`poComplete === true`), never on one INHERITED from
  // the stored note. A note holding both clauses at once cannot come from the submit,
  // but legacy and hand-edited notes hold all sorts of things — and silently deleting
  // a recorded shortfall because of a contradiction this screen merely READ, on an
  // edit that never went near the tick, would be exactly the kind of quiet data loss
  // this module exists to prevent. Inherited completion still re-derives the clause
  // (so it is not duplicated); it just does not get to overrule the shortfall.
  const shortfall = poComplete === true
    ? ''
    : (missingOverride == null ? stored.missing : missingOverride.trim());

  return [derived, shortfall, stored.manual, complete ? PO_COMPLETE_CLAUSE : '']
    .filter((part) => part.length > 0)
    .join(CLAUSE_SEPARATOR);
}

/**
 * Inverts `receipt-rows.rules.ts`: turns stored receipt rows back into the
 * `CheckableLineItem` rows (plus damage lookup) that `buildItemsString` reads.
 *
 * PO rows are grouped by `poLineSequence` — one PO line can own several receipt
 * rows, because a ragged (parts) line writes one row PER PART. Off-PO rows
 * (`poLineSequence: null`) each stay their own row and get a synthetic negative
 * `line`, matching the convention the Items step uses for off-PO adds so
 * `lineKey` stays unique across the set.
 *
 * The one piece of arithmetic here is worth reading twice. A row's stored
 * `quantityReceived` is the NET kept amount: at submit, `applyDamage` already
 * subtracted any refused/returned units from it. `buildItemsString` however
 * reasons about ARRIVED-vs-net (a countable Complete line reads as a bare line
 * number when the two agree and as "(Xn)" when they don't). So the arrived
 * quantity is reconstructed as `quantityReceived + refused-or-returned damaged
 * units`, and the damage is then fed back through `damageByLine` — which
 * subtracts exactly those units again. Feeding the damage in WITHOUT adding them
 * back would double-subtract and understate what arrived; adding them back
 * without feeding the damage in would lose the "(DAMAGED, …)" qualifier.
 */
export function receiptRowsToNoteLines(rows: NoteReceiptRow[]): ReconstructedNoteLines {
  const lineItemRows: CheckableLineItem[] = [];
  const damageByLine: Record<string, LineDamage> = {};

  // Each reconstructed row is recorded together with the receipt rows it came
  // from, so its damage entry is keyed off exactly those rows and nothing has to
  // map a `line` number back onto the source array afterwards.
  const record = (row: CheckableLineItem, group: NoteReceiptRow[]): void => {
    lineItemRows.push(row);
    const damage = damageFor(group);
    if (damage) { damageByLine[lineKey(row)] = damage; }
  };

  for (const [poLineSequence, group] of groupPoRowsByLine(rows ?? [])) {
    record(poLineRow(poLineSequence, group), group);
  }

  let offPoLine = 0;
  for (const row of rows ?? []) {
    if (row.poLineSequence != null) { continue; }
    offPoLine--;
    record(offPoRow(offPoLine, row), [row]);
  }

  return { lineItemRows, damageByLine };
}

/** PO rows keyed by line sequence, insertion-ordered so the note lists lines as the ledger holds them. */
function groupPoRowsByLine(rows: NoteReceiptRow[]): Map<number, NoteReceiptRow[]> {
  const groups = new Map<number, NoteReceiptRow[]>();
  for (const row of rows) {
    if (row.poLineSequence == null) { continue; }
    const existing = groups.get(row.poLineSequence);
    if (existing) { existing.push(row); } else { groups.set(row.poLineSequence, [row]); }
  }
  return groups;
}

/**
 * One PO line's reconstructed row.
 *
 * `isClosed: true` is NOT a claim about the PO line — it is the switch that
 * makes `isOutstandingLine` false for every reconstructed row, so the MISSING
 * clause is never computed from data this screen does not have (file header).
 * Removing it silently starts emitting fabricated shortfall quantities.
 */
function poLineRow(poLineSequence: number, group: NoteReceiptRow[]): CheckableLineItem {
  const first = group[0];
  const status = groupStatus(group);
  const ragged = isRaggedGroup(group);

  const row: CheckableLineItem = {
    line: poLineSequence,
    poLineSequence,
    description: first.itemDescription ?? '',
    itemNumber: first.itemNumber ?? '',
    unit: first.unitOfMeasure ?? '',
    // Left blank on purpose: `expectedQty` reads totalQty/quantity, and a value
    // in either would let the MISSING math run on a fabricated expected amount.
    quantity: '',
    totalQty: '',
    receivedQty: '',
    status,
    completesLine: group.some((r) => r.completesLine === true),
    // See the doc comment above — suppression of the MISSING clause, not a PO claim.
    isClosed: true,
  };

  if (status == null) {
    // A human-close row: nothing physically arrived, so it contributes no clause
    // at all (the arrived list only takes complete/partial).
    return row;
  }

  if (ragged) {
    // Parts, not a count. Round-tripping the descriptions through the same
    // comma-joined text the user typed is what `splitRaggedParts` expects, and
    // it is also what makes `arrivedQty` return null for this line.
    row.partsReceived = group
      .map((r) => (r.itemDescription ?? '').trim())
      .filter((d) => d.length > 0)
      .join(', ');
    return row;
  }

  const arrived = arrivedQtyFor(group);
  if (status === 'complete') {
    // `arrivedQty` falls through to Number(quantity) when the ledger rollup
    // fields are absent, which they are here.
    row.quantity = String(arrived);
  } else {
    row.receivedQty = String(arrived);
  }
  return row;
}

/** One off-PO row. Its ADDITIONAL ITEMS clause reads `quantity`, which is the ARRIVED (pre-refusal) amount. */
function offPoRow(syntheticLine: number, row: NoteReceiptRow): CheckableLineItem {
  const arrived = arrivedQtyFor([row]);
  return {
    line: syntheticLine,
    poLineSequence: null,
    description: row.itemDescription ?? '',
    itemNumber: row.itemNumber ?? '',
    unit: row.unitOfMeasure ?? '',
    quantity: row.quantityReceived != null ? String(arrived) : '',
    totalQty: '',
    receivedQty: '',
    status: 'complete',
    completesLine: false,
    isOffPo: true,
    isClosed: true,
  };
}

/**
 * A group's arrival status. `Partial` wins over `Complete` (a line the user
 * split across both is still a partial arrival), and a group of status-only
 * human-close rows has no arrival status at all.
 */
function groupStatus(group: NoteReceiptRow[]): 'complete' | 'partial' | null {
  if (group.some((r) => r.receiptStatus === 'Partial')) { return 'partial'; }
  if (group.some((r) => r.receiptStatus === 'Complete')) { return 'complete'; }
  return null;
}

/**
 * Whether a PO line arrived as PARTS rather than a count: more than one receipt
 * row for the line, or a single row carrying no quantity. Both are what
 * `receipt-rows.rules.ts` writes for a ragged line, and both must read back as
 * "no countable arrival".
 */
function isRaggedGroup(group: NoteReceiptRow[]): boolean {
  return group.length > 1 || group.some((r) => r.quantityReceived == null);
}

/**
 * What physically ARRIVED on a countable group, before any refusal: the stored
 * net quantity plus the damaged units that were refused or returned (which
 * `applyDamage` had already deducted at submit time).
 */
function arrivedQtyFor(group: NoteReceiptRow[]): number {
  let arrived = 0;
  for (const row of group) {
    arrived += Number(row.quantityReceived ?? 0) || 0;
    if (row.damageDisposition === 'Refuse' || row.damageDisposition === 'Return') {
      arrived += Number(row.damagedQuantity ?? 0) || 0;
    }
  }
  return arrived;
}

/**
 * The damage report for a reconstructed row, or null when none of the receipt
 * rows behind it was flagged damaged. `photos` is always empty — the note
 * composition never reads it, and this screen's damage photos come from their
 * own endpoint.
 *
 * A ragged line can spread damage across several part rows, so the quantity is
 * summed and the first non-empty disposition/note wins (submit stamps the same
 * disposition and note onto every row a damaged line produces, so there is only
 * ever one distinct value to pick).
 *
 * Exported because `log-edit-line-items.rules.ts` needs exactly this, over the
 * loaded receipt rows rather than reconstructed ones — it held a byte-identical
 * copy called `damageFrom`, comments included. `NoteReceiptRow` is a subset of
 * `ReceivingLogEditReceipt`, so the loaded rows satisfy it as they are.
 */
export function damageFor(group: readonly NoteReceiptRow[]): LineDamage | null {
  const damaged = group.filter((r) => r.isDamaged === true);
  if (damaged.length === 0) { return null; }

  const quantity = damaged.reduce((sum, r) => sum + (Number(r.damagedQuantity ?? 0) || 0), 0);
  const withDisposition = damaged.find((r) => r.damageDisposition != null);
  const withNote = damaged.find((r) => (r.damageNote ?? '').trim().length > 0);

  return {
    quantity,
    disposition: withDisposition?.damageDisposition as LineDamage['disposition'] ?? null,
    note: withNote?.damageNote ?? '',
    photos: [],
  };
}
